import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import {
  CalendarIcon,
  MapPinIcon,
  UsersIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";

const Cronologia = () => {
  const { activeCongregazione } = useAuth();
  const [turni, setTurni] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    dataInizio: "",
    dataFine: "",
    postazione: "",
    volontario: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 15,
    total: 0,
    pages: 1,
  });
  const [stats, setStats] = useState({
    totali: 0,
    completati: 0,
    cancellati: 0,
    postazioni: 0,
    volontari: 0,
    range: { inizio: "", fine: null },
  });
  const [selectedTurno, setSelectedTurno] = useState(null);

  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set("page", pagination.page);
    params.set("limit", pagination.limit);
    if (filters.dataInizio) params.set("data_inizio", filters.dataInizio);
    if (filters.dataFine) params.set("data_fine", filters.dataFine);
    if (filters.postazione)
      params.set("postazione", filters.postazione.trim());
    if (filters.volontario)
      params.set("volontario", filters.volontario.trim());
    return params.toString();
  }, [filters, pagination.page, pagination.limit]);

  const loadCronologia = useCallback(async () => {
    if (!activeCongregazione?.id) {
      setTurni([]);
      setStats({
        totali: 0,
        completati: 0,
        cancellati: 0,
        postazioni: 0,
        volontari: 0,
      });
      return;
    }

    setLoading(true);
    try {
      const query = buildQueryParams();
      const response = await api.get(`/cronologia?${query}`);
      setTurni(response.data?.cronologia || []);
      if (response.data?.pagination) {
        const incoming = {
          page: response.data.pagination.page,
          limit: response.data.pagination.limit,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages,
        };
        setPagination((prev) => {
          if (
            prev.page === incoming.page &&
            prev.limit === incoming.limit &&
            prev.total === incoming.total &&
            prev.pages === incoming.pages
          ) {
            return prev;
          }
          return incoming;
        });
      }
      if (response.data?.stats) {
      setStats({
        totali: response.data.stats.totali || 0,
        completati: response.data.stats.completati || 0,
        cancellati: response.data.stats.cancellati || 0,
        postazioni: response.data.stats.postazioni || 0,
        volontari: response.data.stats.volontari || 0,
        range: response.data.stats.range || {
          inizio: "",
          fine: null,
        },
      });
      }
    } catch (error) {
      console.error("Errore cronologia:", error);
      const message =
        error.response?.data?.message ||
        "Impossibile recuperare la cronologia";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [activeCongregazione?.id, buildQueryParams]);

  useEffect(() => {
    loadCronologia();
  }, [loadCronologia]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [activeCongregazione?.id]);


  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({
      dataInizio: "",
      dataFine: "",
      postazione: "",
      volontario: "",
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > pagination.pages) return;
    setPagination((prev) => ({ ...prev, page: nextPage }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("it-IT", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusColor = (stato) => {
    switch (stato) {
      case "completato":
        return "bg-green-100 text-green-800";
      case "cancellato":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getSessoIcon = (sesso) => {
    return sesso === "M" ? "👨" : "👩";
  };

  const handleExportPDF = () => {
    // Funzione per esportare in PDF
    console.log("Esportazione PDF...");
  };

  const handleViewDetails = (turno) => {
    setSelectedTurno(turno);
  };

  const filteredTurni = turni;

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!activeCongregazione?.id) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl border border-amber-200 p-5 text-amber-800">
          Seleziona una congregazione attiva per visualizzare la cronologia dei
          turni.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cronologia</h1>
          <p className="text-gray-600 mt-1">
            Visualizzazione dei turni passati e statistiche
          </p>
          <div className="accent-line mt-4" />
        </div>
        <div className="flex space-x-3 md:space-x-3 w-full md:w-auto">
          <button
            onClick={handleExportPDF}
            className="btn-secondary flex items-center justify-center w-full md:w-auto"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            Esporta PDF
          </button>
        </div>
      </div>
      <p className="text-sm text-gray-500">
        La cronologia mostra gli ultimi tre mesi di attività (dal{" "}
        {stats.range.inizio || "..."}) e i dati più vecchi vengono rimossi
        automaticamente.
      </p>

      {/* Statistiche */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative overflow-hidden">
          <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-blue-100">
              <CalendarIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Turni Totali</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.totali}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative overflow-hidden">
          <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-green-100">
              <UsersIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completati</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.completati}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative overflow-hidden">
          <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-purple-100">
              <MapPinIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Postazioni</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.postazioni}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative overflow-hidden">
          <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-yellow-100">
              <UsersIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Volontari Coinvolti
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.volontari}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 relative overflow-hidden">
        <span className="accent-bar top-4 bottom-4" aria-hidden="true" />
        <div className="flex flex-col sm:flex-row items-start sm:items-center mb-4 gap-2">
          <div className="flex items-center">
            <FunnelIcon className="h-5 w-5 mr-2 text-gray-500" />
            <h3 className="text-lg font-medium text-gray-900">Filtri</h3>
          </div>
          <button
            onClick={handleResetFilters}
            className="text-sm text-primary-600 hover:text-primary-800"
          >
            Pulisci
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="form-label">Data Inizio</label>
            <input
              type="date"
              className="form-input"
              value={filters.dataInizio}
              onChange={(e) => handleFilterChange("dataInizio", e.target.value)}
            />
          </div>
          <div>
            <label className="form-label">Data Fine</label>
            <input
              type="date"
              className="form-input"
              value={filters.dataFine}
              onChange={(e) => handleFilterChange("dataFine", e.target.value)}
            />
          </div>
          <div>
            <label className="form-label">Postazione</label>
            <input
              type="text"
              placeholder="Cerca postazione..."
              className="form-input"
              value={filters.postazione}
              onChange={(e) =>
                handleFilterChange("postazione", e.target.value)
              }
            />
          </div>
          <div>
            <label className="form-label">Volontario</label>
            <input
              type="text"
              placeholder="Cerca volontario..."
              className="form-input"
              value={filters.volontario}
              onChange={(e) =>
                handleFilterChange("volontario", e.target.value)
              }
            />
          </div>
        </div>
      </div>

      {/* Lista Turni */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Turni Passati ({pagination.total})
          </h3>
        </div>
        <div className="hidden lg:block">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Data</th>
                <th className="table-header">Postazione</th>
                <th className="table-header">Orari</th>
                <th className="table-header min-w-[150px]">Volontari</th>
                <th className="table-header">Stato</th>
                <th className="table-header">Azioni</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTurni.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-10 text-center text-sm text-gray-500"
                  >
                    Nessun turno trovato nel periodo selezionato.
                  </td>
                </tr>
              )}
              {filteredTurni.map((turno) => (
                <tr key={turno.id} className="hover:bg-gray-50">
                  <td className="table-cell relative pl-6">
                    <span
                      className="accent-bar top-3 bottom-3"
                      aria-hidden="true"
                    />
                    <div className="text-sm text-gray-900">
                      {formatDate(turno.data)}
                    </div>
                  </td>
                  <td className="table-cell">
                    <div>
                      <div className="font-medium text-gray-900">
                        {turno.postazione}
                      </div>
                      <div className="text-sm text-gray-500">
                        {turno.indirizzo}
                      </div>
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="text-sm text-gray-900">
                      {turno.orario_inizio} - {turno.orario_fine}
                    </div>
                  </td>
                  <td className="table-cell min-w-[150px]">
                    <div className="flex flex-wrap gap-1">
                      {turno.volontari.map((volontario, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center text-xs"
                        >
                          <span className="mr-1">
                            {getSessoIcon(volontario.sesso)}
                          </span>
                          {volontario.nome} {volontario.cognome}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="table-cell">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        turno.stato
                      )}`}
                    >
                      {turno.stato === "completato"
                        ? "Completato"
                        : "Cancellato"}
                    </span>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewDetails(turno)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Visualizza dettagli"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="lg:hidden divide-y divide-gray-200">
          {filteredTurni.length === 0 && (
            <div className="p-6 text-center text-sm text-gray-500">
              Nessun turno trovato nel periodo selezionato.
            </div>
          )}
          {filteredTurni.map((turno) => (
            <div
              key={turno.id}
              className="p-4 pl-6 space-y-2 relative"
            >
              <span className="accent-bar top-3 bottom-3" aria-hidden="true" />
              <div className="text-sm font-semibold text-gray-900">
                {formatDate(turno.data)}
              </div>
              <div>
                <p className="text-base font-medium text-gray-900">
                  {turno.postazione}
                </p>
                <p className="text-sm text-gray-500">{turno.indirizzo}</p>
              </div>
              <p className="text-sm text-gray-700">
                {turno.orario_inizio} - {turno.orario_fine}
              </p>
              <div className="text-sm text-gray-700">
                <span className="font-medium">Volontari:</span>{" "}
                {turno.volontari
                  .map((v) => `${v.nome} ${v.cognome}`.trim())
                  .join(", ")}
              </div>
              <div className="flex items-center justify-between">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                    turno.stato
                  )}`}
                >
                  {turno.stato === "completato" ? "Completato" : "Cancellato"}
                </span>
                <button
                  onClick={() => handleViewDetails(turno)}
                  className="text-primary-600 hover:text-primary-900 text-sm flex items-center"
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  Dettagli
                </button>
              </div>
            </div>
          ))}
        </div>
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-600">
              Pagina {pagination.page} di {pagination.pages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn-secondary disabled:opacity-50"
              >
                Precedente
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="btn-secondary disabled:opacity-50"
              >
                Successiva
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal Dettagli Turno */}
      {selectedTurno && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white overflow-hidden">
            <span className="accent-bar top-5 bottom-5" aria-hidden="true" />
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Dettagli Turno
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="form-label">Data</label>
                  <p className="text-gray-900">
                    {formatDate(selectedTurno.data)}
                  </p>
                </div>
                <div>
                  <label className="form-label">Postazione</label>
                  <p className="text-gray-900">{selectedTurno.postazione}</p>
                  <p className="text-sm text-gray-500">
                    {selectedTurno.indirizzo}
                  </p>
                </div>
                <div>
                  <label className="form-label">Orari</label>
                  <p className="text-gray-900">
                    {selectedTurno.orario_inizio} - {selectedTurno.orario_fine}
                  </p>
                </div>
                <div>
                  <label className="form-label">Volontari</label>
                  <div className="space-y-1">
                    {selectedTurno.volontari.map((volontario, index) => (
                      <div key={index} className="flex items-center">
                        <span className="mr-2">
                          {getSessoIcon(volontario.sesso)}
                        </span>
                        <span className="text-gray-900">
                          {volontario.nome} {volontario.cognome}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="form-label">Stato</label>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                      selectedTurno.stato
                    )}`}
                  >
                    {selectedTurno.stato === "completato"
                      ? "Completato"
                      : "Cancellato"}
                  </span>
                </div>
                {selectedTurno.note && (
                  <div>
                    <label className="form-label">Note</label>
                    <p className="text-gray-900">{selectedTurno.note}</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setSelectedTurno(null)}
                  className="btn-secondary"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cronologia;
